import React, {
    useEffect,
    useState
} from "react";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import {
    ThemeProvider
} from "../v2/home/theme/ThemeProvider";

import { ASSET_LOST_TYPE_URL } from "../../Constants/Constants";

const AssetLostTypeForm = ({
    open,
    onClose,
    editData = null,
    onSaved
}) => {

    const showAlert = useAlert();

    const [saving, setSaving] =
        useState(false);

    const [formData, setFormData] =
        useState({
            code: "",
            name: "",
            description: "",
            enabled: true
        });


    useEffect(() => {

        if (editData) {

            setFormData({
                code: editData.code ?? "",
                name: editData.name ?? "",
                description:
                    editData.description ?? "",

                // IMPORTANT:
                // false must remain false
                enabled:
                    editData.enabled ?? true
            });

        } else {

            setFormData({
                code: "",
                name: "",
                description: "",
                enabled: true
            });
        }

    }, [editData, open]);


    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    const handleEnabledChange = (event) => {

        setFormData(prev => ({
            ...prev,
            enabled:
                event.target.value === "true"
        }));
    };


    const validate = () => {

        if (!formData.code.trim()) {

            showAlert(
                "Lost Type code is required",
                "error"
            );

            return false;
        }

        if (!formData.name.trim()) {

            showAlert(
                "Lost Type name is required",
                "error"
            );

            return false;
        }

        return true;
    };


    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        try {

            setSaving(true);

            const payload = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                description:
                    formData.description.trim(),
                enabled: formData.enabled
            };

            let response;

            if (editData?.id) {

                response =
                    await AxiosInstance.put(
                        `${ASSET_LOST_TYPE_URL}/${editData.id}`,
                        payload
                    );

            } else {

                response =
                    await AxiosInstance.post(
                        ASSET_LOST_TYPE_URL,
                        payload
                    );
            }

            showAlert(
                editData
                    ? "Lost Type updated successfully"
                    : "Lost Type created successfully",
                "success"
            );

            onSaved?.(response.data);

            onClose();

        } catch (error) {

            console.error(error);

            showAlert(
                error?.response?.data?.message ||
                "Unable to save Lost Type",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "space-between"
                }}
            >

                {editData
                    ? "Edit Lost Type"
                    : "Add Lost Type"}

                <IconButton
                    onClick={onClose}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>

            </DialogTitle>


            <DialogContent dividers>

                <ThemeProvider>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection:
                                "column",
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Code"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            autoFocus
                        />

                        <TextField
                            fullWidth
                            label="Lost Type Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />

                        <FormControl>

                            <FormLabel>
                                Enabled
                            </FormLabel>

                            <RadioGroup
                                row
                                value={
                                    formData.enabled
                                        ? "true"
                                        : "false"
                                }
                                onChange={
                                    handleEnabledChange
                                }
                            >

                                <FormControlLabel
                                    value="true"
                                    control={
                                        <Radio />
                                    }
                                    label="Yes"
                                />

                                <FormControlLabel
                                    value="false"
                                    control={
                                        <Radio />
                                    }
                                    label="No"
                                />

                            </RadioGroup>

                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            name="description"
                            value={
                                formData.description
                            }
                            onChange={handleChange}
                        />

                    </Box>

                </ThemeProvider>

            </DialogContent>


            <DialogActions
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving
                        ? "Saving..."
                        : editData
                            ? "Update"
                            : "Save"}
                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default AssetLostTypeForm;